<template>
  <div
    class="custom-chip"
    :class="{
      'chip-small': size === 'small',
      'chip-medium': size === 'medium',
      'chip-large': size === 'large',
      'chip-disabled': disabled,
      'chip-removable': removable && !disabled,
      rounded: shape === 'rounded',
      square: shape === 'square',
      default: type === 'default',
      primary: type === 'primary',
    }"
    :style="customColorStyle"
  >
    <span class="chip-label">
      <slot>{{ label }}</slot>
    </span>
    <button
      v-if="removable && !disabled"
      @click="$emit('remove')"
      class="chip-remove-btn"
      type="button"
      aria-label="Remove"
    >
      <svg class="chip-remove-icon" viewBox="0 0 24 24" fill="currentColor">
        <path
          d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
        />
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  label?: string;
  size?: 'small' | 'medium' | 'large';
  shape?: 'rounded' | 'square' | 'default';
  type?: 'default' | 'primary';
  disabled?: boolean;
  removable?: boolean;
  customColor?: string;
}

interface Emits {
  (e: 'remove'): void;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  disabled: false,
  removable: false,
  shape: 'default',
  type: 'default',
});

defineEmits<Emits>();

const customColorStyle = computed(() => {
  if (!props.customColor) return {};
  return {
    borderColor: props.customColor,
    color: props.customColor,
    border: `1px solid ${props.customColor}`,
  };
});
</script>

<style scoped>
.custom-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--ion-input-background);
  color: var(--ion-text-color);
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: fit-content;
  overflow: hidden;
}

.custom-chip.default {
  background: var(--ion-input-background);
  color: var(--ion-text-color);
  border: 1px solid var(--ion-input-border-color-hover);
}

.custom-chip.primary {
  background: var(--ion-color-primary);
  color: var(--ion-text-color-contrast);
  font-weight: 600;
}
.rounded {
  border-radius: 16px;
}
.square {
  border-radius: 4px;
}

.chip-small {
  padding: 6px 10px;
  font-size: 12px;
  min-height: 24px;
}

.chip-medium {
  padding: 6px 12px;
  font-size: 14px;
  min-height: 32px;
}

.chip-large {
  padding: 8px 16px;
  font-size: 16px;
  min-height: 40px;
}

.chip-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
  color: inherit;
}

.chip-remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  color: currentColor;
  transition: all 0.2s ease;
  flex-shrink: 0;
  color: var(--ion-text-color-contrast);
}

.chip-remove-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  transform: scale(1.1);
}

.chip-remove-icon {
  width: 14px;
  height: 14px;
}

.chip-small .chip-remove-icon {
  width: 12px;
  height: 12px;
}

.chip-large .chip-remove-icon {
  width: 16px;
  height: 16px;
}

.chip-disabled {
  opacity: 0.6;
  pointer-events: none;
  filter: grayscale(0.3);
}

.chip-removable:hover .chip-remove-btn {
  background: var(--ion-chip-border-color-hover);
  color: #dc2626;
}

/* Focus styles for accessibility */
.chip-remove-btn:focus {
  outline: 2px solid rgba(59, 130, 246, 0.5);
  outline-offset: 1px;
}
</style>
